import { getChatUrl } from "@/lib/config";
import { ChatMessage, CustomUIDataTypes } from "@/lib/types";
import { fetchWithErrorHandlers } from "@/lib/utils";
import { useChat } from "@ai-sdk/react";
import { DataUIPart, DefaultChatTransport } from "ai";
import { Bot } from "lucide-react";
import React, { useState } from "react";
import { useSWRConfig } from "swr";
import { Messages } from "./messages";
import { PromptInput } from "./prompt-input";
import { useDataStream } from "./ui/data-stream-provider";

export function Chat() {
  const { messages, sendMessage, status, stop, error } = useChat<ChatMessage>({
    transport: new DefaultChatTransport({
      api: getChatUrl(),
      fetch: fetchWithErrorHandlers,
      prepareSendMessagesRequest({ messages, id, body }) {
        return {
          body: {
            id,
            message: messages.at(-1),
            ...body,
          },
        };
      },
    }),
    onToolCall: (toolCall) => {
      // Handle tool call here
      console.log("Tool call:", toolCall);
    },
    onData: (dataPart: DataUIPart<CustomUIDataTypes>) => {
      setDataStream((ds) => [...(ds || []), dataPart]);
    },
    onFinish: () => {
      // mutate(unstable_serialize(getChatHistoryPaginationKey));
    },
  });

  const { mutate } = useSWRConfig();
  const { setDataStream } = useDataStream();

  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<
    Array<{ name: string; url: string; contentType?: string }>
  >([]);

  const isLoading = status === "submitted" || status === "streaming";

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    sendMessage({
      role: "user",
      parts: [{ type: "text", text: input }],
    });
    setInput("");
  };

  return (
    <div className="flex flex-col h-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <Bot className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Smart POS AI Assistant</h1>
            <p className="text-xs text-muted-foreground">Powered by AI</p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <Messages messages={messages} status={status} className="px-6" />

      {/* Error Display */}
      {error && (
        <div className="px-6 pb-4 flex-shrink-0">
          <div className="rounded-lg p-4 bg-destructive/10 text-destructive border border-destructive/20">
            <p className="text-sm font-medium">Error: {error.message}</p>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="px-6 py-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex-shrink-0">
        <PromptInput
          input={input}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onStop={stop}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          placeholder="Type your message..."
          maxRows={5}
        />
      </div>
    </div>
  );
}
